import type { Profile, Scored, Speed, Track } from "./types";
import { TYPICAL_TRACK_TAGS, normalizeTags, rarity, tagAffinity } from "./vocab";
import { makeRng, softmaxPick, type Rng } from "./random";
import { EMPTY_TASTE, tasteScore, type Taste } from "./taste";

const SPEEDS: Speed[] = ["verylow", "low", "medium", "high", "veryhigh"];

/** Насколько темп трека близок к целевому: точно 1, рядом 0.5, дальше 0. */
function speedScore(target: Speed | undefined, actual: string | undefined): number {
  if (!target || !actual) return 0;
  const a = SPEEDS.indexOf(target);
  const b = SPEEDS.indexOf(actual as Speed);
  if (a < 0 || b < 0) return 0;
  const d = Math.abs(a - b);
  return d === 0 ? 1 : d === 1 ? 0.5 : 0;
}

function trackTags(t: Track): string[] {
  const m = t.musicinfo?.tags;
  return normalizeTags([...(m?.genres ?? []), ...(m?.instruments ?? []), ...(m?.vartags ?? [])]);
}

/**
 * Оценка кандидатов против профиля.
 *
 * Главное отличие от оригинала: он брал `_.shuffle().first()` из 50 строк.
 * Здесь каждый кандидат получает оценку, и берётся лучший — а `matched`
 * сохраняет, по каким терминам совпадение произошло, чтобы выбор можно было
 * объяснить.
 */
/**
 * Насколько усталость перевешивает попадание в профиль.
 *
 * Оценка живёт примерно в диапазоне 0..5.4, так что 4.0 за свежую усталость —
 * это «только что игравший трек проигрывает почти любому неслышанному».
 *
 * Именно штраф, а не исключение: если пул исчерпан (узкий профиль, долгая
 * сессия, молчащий Jamendo), исключение оставило бы тишину, а штраф просто
 * поднимет наверх то, что слышано давнее всего. Деградирует, а не ломается.
 */
const FATIGUE_WEIGHT = 4.0;

/**
 * Усталость от артиста весит меньше: другая вещь того же автора — не повтор,
 * а лишь повод при прочих равных выбрать кого-то ещё.
 */
const ARTIST_FATIGUE_WEIGHT = 2.0;

/**
 * Сколько весит приязнь к тегам трека.
 *
 * Оценка попадания даёт до 5 баллов, вкус — до 0.9 по модулю. Это намеренно:
 * продукт озвучивает погоду, а не крутит любимое. Вкус решает между близкими
 * кандидатами и не может перебить попадание в день.
 */
const TASTE_WEIGHT = 0.6;

export interface Memory {
  /** Усталость по трекам. */
  fatigue?: Map<string, number>;
  /** Усталость по артистам. */
  artists?: Map<string, number>;
  /** Что этот слушатель любит и что пропускает. */
  taste?: Taste;
  now?: number;
}

export function rank(
  candidates: Track[],
  p: Profile,
  memory: Memory = {},
): Scored[] {
  const listens = candidates.map((t) => Number(t.stats?.listens_total ?? 0));
  const maxListens = Math.max(1, ...listens);

  // Достижимый максимум не зависит от трека: сумма нескольких самых ценных
  // терминов профиля. Считался внутри цикла для каждого из шестисот
  // кандидатов, с сортировкой на каждом шаге.
  const reachable = Math.min(TYPICAL_TRACK_TAGS, p.tags.size);
  const bestReachable = [...p.tags]
    .map(([tag, w]) => w * rarity(tag))
    .sort((a, b) => b - a)
    .slice(0, reachable)
    .reduce((a, b) => a + b, 0);

  const scored = candidates.map((t): Scored => {
    const tags = trackTags(t);

    // Совпадение взвешивается редкостью тега: попасть в `neutral` (186 из 800
    // треков выборки) почти ничего не значит, попасть в `melancholic` (10 из 800)
    // значит много. Без этого ранжирование сползает к самым общим тегам.
    //
    // Засчитывается и родня — `blues` за `jazz`, — но долей веса, а не целиком.
    // Раньше здесь стояло сравнение подстрокой, и оно отдавало полный вес
    // редкого термина его частому куску: `acoustic` (402 из 800) получал вес
    // `acousticguitar` (9 из 800). См. KIN_PAIRS в vocab.ts.
    let tagSum = 0;
    const matchedTags: string[] = [];
    for (const [tag, w] of p.tags) {
      let affinity = 0;
      for (const x of tags) {
        const a = tagAffinity(tag, x);
        if (a > affinity) affinity = a;
        if (affinity === 1) break;
      }
      if (affinity > 0) {
        tagSum += w * rarity(tag) * affinity;
        matchedTags.push(tag);
      }
    }
    // Трек несёт медианно 3 тега (замер по каталогу), поэтому совпасть со всеми
    // терминами профиля он не может физически. Нормируем на достижимый максимум,
    // иначе все оценки жмутся к нулю и решает уже не профиль, а популярность.
    const tagsPart = bestReachable > 0 ? Math.min(1, tagSum / bestReachable) : 0;

    const speedPart = speedScore(p.speed, t.musicinfo?.speed);
    const vocalOk = !!p.vocal && t.musicinfo?.vocalinstrumental === p.vocal;
    const acousticOk = !!p.acoustic && t.musicinfo?.acousticelectric === p.acoustic;

    // Популярность — только разрешение ничьей, не движущая сила.
    const popPart = Math.log1p(Number(t.stats?.listens_total ?? 0)) / Math.log1p(maxListens);

    const parts = {
      tags: tagsPart,
      speed: speedPart,
      vocal: vocalOk ? 1 : 0,
      acoustic: acousticOk ? 1 : 0,
      pop: popPart,
    };

    // Длина: мягкий штраф за выход из окна.
    const [lo, hi] = p.duration;
    const durOk = t.duration >= lo && t.duration <= hi ? 0 : -0.5;

    /**
     * Оценка усредняется по тем осям, которые у трека ВООБЩЕ ЕСТЬ.
     *
     * У Internet Archive нет `speed`, `vocalinstrumental` и `acousticelectric`.
     * При простой сумме такой трек терял бы 3.2 балла из 6.6 просто за то, что
     * поля не заполнены, и не выигрывал бы никогда — второй пул существовал бы
     * только на бумаге. Поэтому недостающая ось не обнуляется, а исключается
     * из знаменателя: сравниваем по тому, что можно сравнить.
     */
    // Ось, про которую у трека нет данных, засчитывается частично.
    //
    // Раньше такая ось просто выбрасывалась из среднего — то есть
    // «неизвестно» приравнивалось к «идеально», и запись вообще без полей
    // обходила трек, промахнувшийся ровно одной осью. У архива не заполнено
    // почти ничего, так что премия доставалась именно худшим метаданным.
    //
    // Полностью штрафовать тоже нельзя: отсутствие поля — это свойство
    // площадки, а не трека, и второй пул иначе не попадал бы в набор вовсе.
    const UNKNOWN = 0.6;
    const axes: [number, number][] = [[3.0, parts.tags]];
    if (p.speed) axes.push([1.5, t.musicinfo?.speed ? parts.speed : UNKNOWN]);
    if (p.vocal) axes.push([1.0, t.musicinfo?.vocalinstrumental ? parts.vocal : UNKNOWN]);
    if (p.acoustic) axes.push([0.7, t.musicinfo?.acousticelectric ? parts.acoustic : UNKNOWN]);

    const weight = axes.reduce((a, [w]) => a + w, 0);
    const fit = axes.reduce((a, [w, v]) => a + w * v, 0) / weight;

    // Популярность и длина — поправки к попаданию, а не отдельные оси.
    // Усталость вычитается последней: она про этого слушателя, а не про трек.
    const tired = memory.fatigue?.get(t.id) ?? 0;
    const tiredArtist = memory.artists?.get(t.artist_id) ?? 0;
    const liking = tasteScore(memory.taste ?? EMPTY_TASTE, tags, memory.now ?? Date.now());
    const score = fit * 5 + 0.4 * parts.pop + durOk + TASTE_WEIGHT * liking
      - FATIGUE_WEIGHT * tired - ARTIST_FATIGUE_WEIGHT * tiredArtist;

    return {
      track: t,
      score,
      matched: { tags: matchedTags, speed: speedPart === 1, vocal: vocalOk, acoustic: acousticOk },
      parts,
      fatigue: tired,
    };
  });

  return scored.sort((a, b) => b.score - a.score);
}

/** Все теги трека одним множеством — для оценки похожести. */
function trackTagSet(t: Track): Set<string> {
  const m = t.musicinfo?.tags;
  return new Set([...(m?.genres ?? []), ...(m?.instruments ?? []), ...(m?.vartags ?? [])]
    .map((x) => x.toLowerCase()));
}

/**
 * Набор на сессию с подавлением похожести.
 *
 * Прежняя версия ограничивала жанры по `genres[0]`, и это не работало: трек
 * без жанровых тегов обходил ограничение вовсе, а `latin`, `reggae` и `ska`
 * считались разными жанрами, хотя это одно настроение. В жаркий ясный день
 * весь набор выходил латино-регги — «что-то однообразное».
 *
 * Теперь каждый следующий трек выбирается не по чистой оценке, а по оценке
 * за вычетом того, насколько он похож на уже отобранные: совпадение тегов,
 * жанра и темпа штрафуется. Это стандартный приём для списков рекомендаций —
 * лучший кандидат уступает место достаточно хорошему, но другому.
 */
export interface BuildOpts {
  diversity?: number;
  rng?: Rng;
  /**
   * 0 — строгий максимум: тот же пул даёт тот же набор. Выше нуля — выбор из
   * почти равных по softmax, то есть одинаковая погода звучит по-разному в
   * разных сессиях. Тесты, которым нужен предсказуемый ответ, оставляют ноль.
   */
  temperature?: number;
}

export function buildSet(scored: Scored[], n = 8, opts: BuildOpts = {}): Scored[] {
  if (!scored.length) return [];
  const { diversity = 0.7, rng = makeRng(1), temperature = 0 } = opts;

  const pool = scored.slice(0, 120);
  const tags = new Map<Scored, Set<string>>(pool.map((s) => [s, trackTagSet(s.track)]));
  const out: Scored[] = [];
  const artists = new Set<string>();
  const taken = new Set<Scored>();

  /**
   * Похожесть по Жаккару — совпавшее к объединению.
   *
   * Делить на МЕНЬШИЙ набор нельзя: трек с одним тегом совпадал тогда с
   * пятитеговым на все 100% и вылетал из набора как дубль. У архива тегов
   * всегда мало, так что это выбивало второй пул целиком. Одинаковые наборы
   * при этом по-прежнему дают единицу — давление на настоящие дубли цело.
   */
  const overlap = (a: Set<string>, b: Set<string>) => {
    if (!a.size || !b.size) return 0;
    let hit = 0;
    for (const x of a) if (b.has(x)) hit++;
    return hit / (a.size + b.size - hit);
  };

  const maxScore = Math.max(...pool.map((s) => s.score), 1);

  while (out.length < n) {
    const cands: Scored[] = [];
    const values: number[] = [];

    for (const s of pool) {
      if (taken.has(s) || artists.has(s.track.artist_id)) continue;

      // Похожесть считается двояко: насколько похож самый близкий из уже
      // отобранных и сколько таких вообще. Одного максимума мало — он
      // упирается в единицу и перестаёт отличать второй почти-дубль от
      // четвёртого, из-за чего набор всё равно съезжал в один жанр.
      let nearest = 0;
      let twins = 0;
      for (const c of out) {
        const t = Math.max(
          overlap(tags.get(s)!, tags.get(c)!),
          s.track.musicinfo?.speed && s.track.musicinfo.speed === c.track.musicinfo?.speed ? 0.35 : 0,
        );
        if (t > nearest) nearest = t;
        if (t >= 0.5) twins++;
      }

      const similarity = nearest + 0.6 * twins;
      cands.push(s);
      values.push(s.score / maxScore - diversity * similarity);
    }

    if (!cands.length) break;
    const best = cands[softmaxPick(values, rng, temperature)]!;
    taken.add(best);
    artists.add(best.track.artist_id);
    out.push(best);
  }
  return out;
}

export interface ExtendOpts {
  /**
   * Ниже этой доли попадания по тегам добавка в очередь не идёт.
   *
   * Второй пул дописывался безусловно: приехало из архива — играем, даже если
   * с погодой это не связано ничем. Очередь тем самым размывалась ровно тем,
   * ради чего весь подбор и затевался.
   */
  minFit?: number;
}

/** Добавить кандидатов к текущему потоку, не повторяя уже сыгранное. */
export function extendSet(current: Scored[], more: Scored[], n = 6, opts: ExtendOpts = {}): Scored[] {
  const seenTracks = new Set(current.map((s) => s.track.id));
  const seenArtists = new Set(current.slice(-6).map((s) => s.track.artist_id));
  const add: Scored[] = [];
  for (const s of more) {
    if (seenTracks.has(s.track.id) || seenArtists.has(s.track.artist_id)) continue;
    if (opts.minFit !== undefined && s.parts.tags < opts.minFit) continue;
    seenArtists.add(s.track.artist_id);
    add.push(s);
    if (add.length >= n) break;
  }
  return [...current, ...add];
}
